import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Plus, Clock, AlarmClock, Check, X, Calendar, CheckSquare, StickyNote, FolderKanban } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useNow } from '../lib/relativeTime';
import { getEntityUrl } from '../lib/entityUrls';
import { ReminderQuickCreate } from '../components/ReminderQuickCreate';

interface Reminder {
  id: string;
  title: string;
  body: string | null;
  remind_at: string;
  recurrence_rule: string | null;
  status: string;
  fired_at: string | null;
  snoozed_until: string | null;
  snooze_count: number;
  entity_type: string | null;
  entity_id: string | null;
  auto_source: string | null;
  created_at: string;
}

const STATUS_TABS = [
  { value: 'active', label: 'Active' },
  { value: 'fired', label: 'Fired' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'completed', label: 'Completed' },
];

const ENTITY_ICONS: Record<string, typeof Calendar> = {
  meeting_instance: Calendar,
  meeting_series: Calendar,
  task: CheckSquare,
  note: StickyNote,
  scope: FolderKanban,
};

function entityUrl(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  return getEntityUrl({ entity_type: type, entity_id: id, name: '' });
}

export function RemindersPage() {
  const toast = useToast();
  useNow();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    try {
      let params = '';
      if (tab === 'active') {
        // Active = pending + snoozed
        const [pending, snoozed] = await Promise.all([
          apiGet<Reminder[]>('/reminders?status=pending'),
          apiGet<Reminder[]>('/reminders?status=snoozed'),
        ]);
        setReminders([...pending, ...snoozed].sort((a, b) => a.remind_at.localeCompare(b.remind_at)));
      } else {
        params = `?status=${tab}`;
        setReminders(await apiGet<Reminder[]>(`/reminders${params}`));
      }
    } catch {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { setLoading(true); load(); }, [tab]);

  async function handleSnooze(id: string, minutes: number) {
    try {
      await apiPost(`/reminders/${id}/snooze`, { minutes });
      toast.success(`Snoozed for ${minutes} minutes`);
      load();
    } catch { toast.error('Failed to snooze'); }
  }

  async function handleDismiss(id: string) {
    try {
      await apiPost(`/reminders/${id}/dismiss`, {});
      toast.success('Reminder dismissed');
      load();
    } catch { toast.error('Failed to dismiss'); }
  }

  async function handleComplete(id: string) {
    try {
      await apiPost(`/reminders/${id}/complete`, {});
      toast.success('Reminder completed');
      load();
    } catch { toast.error('Failed to complete'); }
  }

  async function handleDelete(id: string) {
    try {
      await apiDelete(`/reminders/${id}`);
      toast.success('Reminder deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  }

  function groupByTime(items: Reminder[]): Record<string, Reminder[]> {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today); nextWeek.setDate(nextWeek.getDate() + 7);

    const groups: Record<string, Reminder[]> = {
      'Overdue': [],
      'Today': [],
      'Tomorrow': [],
      'This Week': [],
      'Later': [],
    };

    for (const r of items) {
      const d = new Date(r.remind_at);
      if (d < now && r.status !== 'snoozed') groups['Overdue'].push(r);
      else if (d < tomorrow) groups['Today'].push(r);
      else if (d < new Date(tomorrow.getTime() + 86400000)) groups['Tomorrow'].push(r);
      else if (d < nextWeek) groups['This Week'].push(r);
      else groups['Later'].push(r);
    }

    return groups;
  }

  const grouped = tab === 'active' ? groupByTime(reminders) : null;

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Bell size={22} className="text-text-muted" />
            <h1 className="text-xl font-bold text-text-primary">Reminders</h1>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            <Plus size={16} /> New Reminder
          </button>
        </div>

        {showCreate && (
          <div className="mb-6">
            <ReminderQuickCreate
              onCreated={() => { setShowCreate(false); load(); }}
              onCancel={() => setShowCreate(false)}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4">
          {STATUS_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                tab === t.value
                  ? 'bg-accent text-white'
                  : 'text-text-secondary hover:bg-bg-hover'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-text-muted text-sm">Loading...</div>
          </div>
        ) : reminders.length === 0 ? (
          <div className="text-center py-16">
            <Bell size={40} className="mx-auto text-text-muted mb-3 opacity-40" />
            <p className="text-text-muted text-sm">
              {tab === 'active' ? 'No upcoming reminders' : `No ${tab} reminders`}
            </p>
          </div>
        ) : tab === 'active' && grouped ? (
          /* Grouped view for active */
          <div className="space-y-6">
            {Object.entries(grouped).map(([group, items]) => {
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
                    group === 'Overdue' ? 'text-danger' : 'text-text-muted'
                  }`}>{group}</h3>
                  <div className="space-y-1">
                    {items.map(r => (
                      <ReminderRow
                        key={r.id}
                        reminder={r}
                        onSnooze={handleSnooze}
                        onDismiss={handleDismiss}
                        onComplete={handleComplete}
                        onDelete={handleDelete}
                        showActions
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Flat list for fired/dismissed/completed */
          <div className="space-y-1">
            {reminders.map(r => (
              <ReminderRow
                key={r.id}
                reminder={r}
                onSnooze={handleSnooze}
                onDismiss={handleDismiss}
                onComplete={handleComplete}
                onDelete={handleDelete}
                showActions={tab === 'fired'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReminderRow({ reminder: r, onSnooze, onDismiss, onComplete, onDelete, showActions }: {
  reminder: Reminder;
  onSnooze: (id: string, minutes: number) => void;
  onDismiss: (id: string) => void;
  onComplete: (id: string) => void;
  onDelete: (id: string) => void;
  showActions: boolean;
}) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const EntityIcon = r.entity_type ? ENTITY_ICONS[r.entity_type] : null;
  const link = entityUrl(r.entity_type, r.entity_id);
  const timeStr = new Date(r.remind_at).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-bg-surface transition-colors group">
      <div className="flex-shrink-0">
        {r.status === 'snoozed' ? (
          <AlarmClock size={16} className="text-warning" />
        ) : r.status === 'completed' ? (
          <Check size={16} className="text-success" />
        ) : (
          <Clock size={16} className="text-text-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary truncate">{r.title}</span>
          {EntityIcon && link && (
            <Link to={link} className="flex-shrink-0 text-text-muted hover:text-accent transition-colors">
              <EntityIcon size={13} />
            </Link>
          )}
          {r.recurrence_rule && (
            <span className="text-[10px] text-accent bg-accent/10 px-1.5 py-0.5 rounded-full flex-shrink-0">recurring</span>
          )}
          {r.auto_source && (
            <span className="text-[10px] text-text-muted bg-bg-input px-1.5 py-0.5 rounded-full flex-shrink-0">auto</span>
          )}
        </div>
        <div className="text-xs text-text-muted mt-0.5">
          {timeStr}
          {r.snooze_count > 0 && <span className="ml-2">snoozed {r.snooze_count}x</span>}
        </div>
      </div>
      {showActions && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
          <div className="relative">
            <button
              onClick={() => setSnoozeOpen(!snoozeOpen)}
              className="p-1 text-text-muted hover:text-warning transition-colors"
              title="Snooze"
            >
              <AlarmClock size={14} />
            </button>
            {snoozeOpen && (
              <div className="absolute right-0 top-7 z-10 bg-bg-surface border border-border rounded-md shadow-lg py-1 w-32">
                {[5, 15, 30, 60, 240].map(m => (
                  <button
                    key={m}
                    onClick={() => { onSnooze(r.id, m); setSnoozeOpen(false); }}
                    className="w-full px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-hover text-left"
                  >
                    {m < 60 ? `${m} min` : `${m / 60} hour${m > 60 ? 's' : ''}`}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => onComplete(r.id)} className="p-1 text-text-muted hover:text-success transition-colors" title="Complete">
            <Check size={14} />
          </button>
          <button onClick={() => onDismiss(r.id)} className="p-1 text-text-muted hover:text-text-primary transition-colors" title="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      {!showActions && (
        <button
          onClick={() => onDelete(r.id)}
          className="p-1 text-text-muted hover:text-danger opacity-0 group-hover:opacity-100 transition-all"
          title="Delete"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
